import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, IconButton, InputAdornment, OutlinedInput, Typography, useMediaQuery } from '@mui/material';
import Modal from '@mui/material/Modal';
import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { sxFormError, sxLabel } from '../../assets/styles/CommonSxProps';
import { Address } from '../../interfaces/entities/Cancelamento/PassoCartaCancelamento/Address';
import { EnderPostal } from '../../interfaces/entities/MeuCadastro/EnderPostal';
import { NewAdressValidation } from '../../interfaces/validation/Cancelamento/NewAdressValidation';
import { getLogradrouroPorCep } from '../../services/BeneficiarioService';
import { useAccessibility } from '../../Store/contexts/AccessibilityContext';
import { cepMask } from '../../utils/CepMask';
import DefaultButton from '../Accessibility/DefaultButton';
import WhiteContainer from '../Accessibility/WhiteContainer';
import './styles.css';
import { sxButton, sxButtonContained, sxInputAddress, sxSelect, sxTypographyTitle } from './sxProps';

const ModalNewAddress: React.FC<{
  showModal: boolean,
  handleShowModal: () => void,
  onSubmit: (data: Address) => void,
  openModalFeedBack: (title: string, subtitle: string) => void,
}> = ({ showModal, handleShowModal, onSubmit, openModalFeedBack }) => {

  const { textStyleAccessibility, buttonStyleAccessibility } = useAccessibility();

  const mobileScreen = useMediaQuery('(max-width: 899px)');
  const [loadingCep, setLoadingCep] = useState<boolean>(false);


  const formik = useFormik({
    initialValues: {
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: ''
    },
    validationSchema: NewAdressValidation,
    onSubmit: (values) => {
      values.cep = values.cep.replace('-', ''); //Remove hífen do CEP, utilizado apenas para formatação
      onSubmit(values);
      formik.resetForm();
    },
    validateOnChange: false
  });

  const mandatoryFieldsNotFilled: boolean = !formik.values.cep || !formik.values.bairro || !formik.values.logradouro ||
    !formik.values.cidade || !formik.values.estado || !formik.values.numero;

  const closeModal = () => {
    handleShowModal();
    formik.resetForm();
  }

  const containedButton = (
    <DefaultButton
      type='submit'
      sx={sxButtonContained}
      variant={'contained'}
      text={'Salvar'}
      disabled={mandatoryFieldsNotFilled}
    />
  );

  const outlinedButton = (
    <DefaultButton
      onClick={closeModal}
      sx={sxButton}
      text={'Cancelar'}
      variant={'outlined'}
    />
  );

  const findAdressByCep = async (cep: string) => {
    setLoadingCep(true);
    const response = await getLogradrouroPorCep(cep.replace('-', ''));
    if (response?.status === 200) {
      if (!!response.data.length) {
        const enderecoCep = response.data;
        updateFormikValues(enderecoCep.at(0)!);
        formik.setFieldError('cep', undefined);
      } else {
        const invalidCepMessage = 'Verifique se o CEP informado é válido';
        formik.setFieldError('cep', invalidCepMessage);
      }
    } else {
      openModalFeedBack('Erro', 'Ocorreu um erro ao procurar pelo CEP, tente novamente em instantes.');
      console.error(response);
    }
    setLoadingCep(false);
  }

  const updateFormikValues = (endereco: EnderPostal) => {
    formik.setFieldValue('logradouro', endereco?.logradouro);
    formik.setFieldValue('bairro', endereco?.bairro);
    formik.setFieldValue('cidade', endereco?.cidade);
    formik.setFieldValue('estado', endereco?.estado);
    formik.setFieldValue('numero', '');
    formik.setFieldValue('complemento', '');
  }


  return (
    <div>
      {showModal && <Modal
        open={showModal}
        onClose={closeModal}
      >
        <div>
        <WhiteContainer className='container-modal-new-address'>
          <div className='modal-new-address-header'>
            <Typography component="h1" style={textStyleAccessibility} sx={sxTypographyTitle}>Cadastrar novo endereço</Typography>
            <IconButton
              aria-label="fechar"
              onClick={closeModal}
              style={buttonStyleAccessibility}
            >
              <FontAwesomeIcon icon={faTimes} size="1x" color='black' />
            </IconButton>
          </div>
          <form onSubmit={formik.handleSubmit} className="form-change-data">
            <div className='new-address-outlined-input'>
              <Typography style={textStyleAccessibility} sx={sxLabel}>CEP</Typography>
              <label htmlFor='novoEndereco' aria-label={`CEP ${formik.errors.cep ?? ''}`} />
              <OutlinedInput
                id='novoEndereco'
                fullWidth={mobileScreen}
                type={'text'}
                disabled={loadingCep}
                value={formik.values.cep}
                onChange={(e) => {
                  const cepValue = e.target.value;
                  formik.setFieldValue('cep', cepMask(cepValue));
                  if (cepValue.replace('-', '').length === 8) {
                    findAdressByCep(cepValue);
                  }
                }}
                name='cep'
                error={!!formik.errors.cep}
                style={textStyleAccessibility}
                maxRows={1}
                inputProps={{ maxLength: 9 }}
                sx={sxInputAddress}
                endAdornment={loadingCep ?
                  (
                    <InputAdornment position="end">
                      <CircularProgress size={20} sx={{ zIndex: '1' }} />
                    </InputAdornment>
                  ) : null
                }
              />
              <Typography style={textStyleAccessibility} sx={sxFormError}>{formik.errors.cep}</Typography>
            </div>
            <div className='new-address-outlined-input'>
              <Typography style={textStyleAccessibility} sx={sxLabel}>Logradouro</Typography>
              <label htmlFor='logradouro' aria-label='Logradouro' />
              <OutlinedInput
                style={textStyleAccessibility}
                id='logradouro'
                type={'text'}
                value={formik.values.logradouro}
                onChange={(e) => { formik.setFieldValue('logradouro', e.target.value); }}
                name='logradouro'
                error={!!formik.errors.logradouro}
                maxRows={1}
                sx={sxInputAddress}
                disabled
              />
              <Typography style={textStyleAccessibility} sx={sxFormError}>{formik.errors.logradouro}</Typography>
            </div>
            <div className="half-divided-input">
              <div className='new-address-outlined-input'>
                <Typography style={textStyleAccessibility} sx={sxLabel}>Número</Typography>
                <label htmlFor='numero' aria-label='Número' />
                <OutlinedInput
                  style={textStyleAccessibility}
                  id='numero'
                  type={'text'}
                  value={formik.values.numero}
                  onChange={(e) => { formik.setFieldValue('numero', e.target.value.toUpperCase()); }}
                  name='numero'
                  error={!!formik.errors.numero}
                  inputProps={{ maxLength: 6 }}
                  maxRows={1}
                  sx={sxInputAddress}
                />
                <Typography style={textStyleAccessibility} sx={sxFormError}>{formik.errors.numero}</Typography>
              </div>
              <div className='new-address-outlined-input'>
                <Typography style={textStyleAccessibility} sx={sxLabel}>Complemento</Typography>
                <label htmlFor='complemento' aria-label='Complemento' />
                <OutlinedInput
                  style={textStyleAccessibility}
                  id='complemento'
                  type={'text'}
                  value={formik.values.complemento}
                  onChange={(e) => { formik.setFieldValue('complemento', e.target.value); }}
                  name='complemento'
                  error={!!formik.errors.complemento}
                  maxRows={1}
                  inputProps={{ maxLength: 15 }}
                  sx={sxInputAddress}
                />
                <Typography style={textStyleAccessibility} sx={sxFormError}>{formik.errors.complemento}</Typography>
              </div>
            </div>
            <div className='new-address-outlined-input'>
              <Typography style={textStyleAccessibility} sx={sxLabel}>Bairro</Typography>
              <label htmlFor='bairro' aria-label='Bairro' />
              <OutlinedInput
                style={textStyleAccessibility}
                id='bairro'
                type={'text'}
                value={formik.values.bairro}
                onChange={(e) => { formik.setFieldValue('bairro', e.target.value); }}
                name='bairro'
                error={!!formik.errors.bairro}
                maxRows={1}
                sx={sxInputAddress}
                disabled
              />
              <Typography sx={sxFormError}>{formik.errors.bairro}</Typography>
            </div>
            <div className="half-divided-input">
              <div className='new-address-outlined-input'>
                <Typography style={textStyleAccessibility} sx={sxLabel}>Cidade</Typography>
                <label htmlFor='cidade' aria-label='cidade' />
                <OutlinedInput
                  id='cidade'
                  style={textStyleAccessibility}
                  type={'text'}
                  value={formik.values.cidade}
                  onChange={(e) => { formik.setFieldValue('cidade', e.target.value); }}
                  name='cidade'
                  error={!!formik.errors.cidade}
                  maxRows={1}
                  fullWidth
                  sx={sxInputAddress}
                  disabled
                />
                <Typography style={textStyleAccessibility} sx={sxFormError}>{formik.errors.cidade}</Typography>
              </div>
              <div className='new-address-outlined-input'>
                <Typography style={textStyleAccessibility} sx={sxLabel}>Estado</Typography>
                <label htmlFor='estado' aria-label='estado' />
                <OutlinedInput
                  id='estado'
                  sx={sxSelect}
                  style={textStyleAccessibility}
                  value={formik.values.estado}
                  onChange={(e) => formik.setFieldValue('estado', e.target.value)}
                  disabled
                />
                <Typography style={textStyleAccessibility} sx={sxFormError}>{formik.errors.estado}</Typography>
              </div>
            </div>
            <div className='new-address-buttons-holder'>
              {mobileScreen ? containedButton : outlinedButton}
              {mobileScreen ? outlinedButton : containedButton}
            </div>
          </form>
        </WhiteContainer>
        </div>
      </Modal>}
    </div>
  );
};

export default ModalNewAddress;
